import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import AppointmentDialog from "@/components/AppointmentDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarPlus, CaretLeft, CaretRight, Stethoscope } from "@phosphor-icons/react";

const ALL = "__all__";
const STATUS_CLS = {
  scheduled: "bg-primary/10 text-primary border-primary/30",
  completed: "bg-success/10 text-success border-success/40",
  cancelled: "bg-muted text-muted-foreground border-border",
  no_show: "bg-destructive/10 text-destructive border-destructive/40",
};
const Badge = ({ s }) => <span className={`inline-flex px-2 py-0.5 rounded-sm border text-xs font-medium capitalize ${STATUS_CLS[s]}`}>{String(s).replace("_", " ")}</span>;

export default function Appointments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [doctorId, setDoctorId] = useState(ALL);
  const [appts, setAppts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const canWrite = user?.permissions?.includes("appointment:write");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date };
      if (doctorId !== ALL) params.doctor_id = doctorId;
      const { data } = await api.get("/clinic/appointments", { params });
      setAppts(data);
    } finally { setLoading(false); }
  }, [date, doctorId]);

  useEffect(() => {
    api.get("/clinic/doctors").then(({ data }) => setDoctors(data)).catch(() => {});
    api.get("/clinic/patients").then(({ data }) => setPatients(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const shift = (days) => { const d = new Date(date); d.setDate(d.getDate() + days); setDate(d.toISOString().slice(0, 10)); };
  const setStatus = async (a, status) => {
    try { await api.patch(`/clinic/appointments/${a.id}`, { status }); toast.success("Updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div data-testid="appointments-page">
      <PageHeader title="Appointments" subtitle="Daily schedule across doctors">
        {canWrite && <Button onClick={() => setBooking(true)} data-testid="book-appt-button" className="bg-primary hover:bg-primary/90 gap-2"><CalendarPlus size={17} weight="bold" /> Book appointment</Button>}
      </PageHeader>

      <div className="p-6 md:p-8 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <Button size="icon" variant="outline" onClick={() => shift(-1)} data-testid="prev-day" className="h-9 w-9"><CaretLeft size={16} /></Button>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="appt-date-filter" className="w-40" />
            <Button size="icon" variant="outline" onClick={() => shift(1)} data-testid="next-day" className="h-9 w-9"><CaretRight size={16} /></Button>
          </div>
          <Select value={doctorId} onValueChange={setDoctorId}>
            <SelectTrigger className="w-56" data-testid="doctor-filter"><SelectValue placeholder="All doctors" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All doctors</SelectItem>
              {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Loading…</div> : appts.length === 0 ? (
          <div className="h-48 flex flex-col items-center justify-center text-muted-foreground gap-2 border border-dashed border-border rounded-md">
            No appointments on this day.
            {canWrite && <Button size="sm" variant="outline" onClick={() => setBooking(true)}>Book one</Button>}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <ul className="divide-y divide-border">
              {appts.map((a) => (
                <li key={a.id} data-testid={`appt-row-${a.id}`} className="px-5 py-3.5 flex flex-wrap items-center justify-between gap-3 hover:bg-accent/30">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-mono font-medium w-14">{a.time}</span>
                    <div>
                      <button onClick={() => navigate(`/clinic/patients/${a.patient_id}`)} className="text-sm font-medium hover:text-primary hover:underline" data-testid={`appt-patient-${a.id}`}>{a.patient_name}</button>
                      <p className="text-xs text-muted-foreground">{a.doctor_name}{a.reason ? ` · ${a.reason}` : ""}{a.patient_phone ? ` · ${a.patient_phone}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge s={a.status} />
                    {a.status === "scheduled" && canWrite && (
                      <Select value={undefined} onValueChange={(v) => setStatus(a, v)}>
                        <SelectTrigger className="h-8 w-32" data-testid={`appt-status-${a.id}`}><SelectValue placeholder="Update" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="no_show">No show</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/clinic/patients/${a.patient_id}`)} className="gap-1.5" data-testid={`appt-open-${a.id}`}><Stethoscope size={14} /> Open</Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <AppointmentDialog open={booking} onOpenChange={setBooking} doctors={doctors} patients={patients} defaultDate={date} onSaved={load} />
    </div>
  );
}
